import type {
  ReactNode,
} from 'react'

import {
  FieldError as AriaFieldError,
  SwitchButton,
  SwitchField,
  Text,
  type SwitchFieldProps,
} from 'react-aria-components'

import type {
  FormFieldErrorMessage,
} from './form-field'

import { cn } from '@/shared/lib/cn'

export interface ToggleSwitchProps
  extends Omit<
    SwitchFieldProps,
    'children' | 'className'
  > {
  label: ReactNode
  description?: ReactNode
  errorMessage?: FormFieldErrorMessage

  className?: string
}

export function ToggleSwitch({
  label,
  description,
  errorMessage,
  isInvalid,
  validationBehavior = 'aria',
  className,
  ...props
}: ToggleSwitchProps) {
  const hasStaticError =
    typeof errorMessage === 'string' &&
    errorMessage.trim().length > 0

  return (
    <SwitchField
      {...props}
      isInvalid={
        isInvalid ?? hasStaticError
      }
      validationBehavior={validationBehavior}
      className={cn(
        [
          'flex flex-col gap-1.5',
          'data-disabled:opacity-60',
        ],
        className,
      )}
    >
      <SwitchButton className="group flex w-fit cursor-pointer items-start gap-3 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-(--color-primary) focus-visible:ring-offset-2 data-disabled:cursor-not-allowed">
        {({ isSelected }) => (
          <>
            <span
              className={cn(
                [
                  'relative mt-0.5 h-6 w-11',
                  'shrink-0 rounded-full',
                  'transition-colors',
                ],
                isSelected
                  ? 'bg-(--color-primary)'
                  : 'bg-(--color-border)',
              )}
            >
              <span
                className={cn(
                  [
                    'absolute top-0.5',
                    'h-5 w-5 rounded-full',
                    'bg-white shadow-sm',
                    'transition-transform',
                  ],
                  isSelected
                    ? 'translate-x-5'
                    : 'translate-x-0.5',
                )}
              />
            </span>

            <span className="text-sm font-medium leading-6">
              {label}
            </span>
          </>
        )}
      </SwitchButton>

      {description ? (
        <Text
          slot="description"
          className="ml-14 text-xs leading-5 text-(--color-muted-foreground)"
        >
          {description}
        </Text>
      ) : null}

      <AriaFieldError className="ml-14 text-xs leading-5 text-(--color-danger)">
        {errorMessage}
      </AriaFieldError>
    </SwitchField>
  )
}
